import { BaseSelection, SelectionExtension } from '@blocksuite/store';
import { z } from 'zod';

const TableAreaSelectionSchema = z.object({
  type: z.literal('area'),
  rowStartIndex: z.number(),
  rowEndIndex: z.number(),
  columnStartIndex: z.number(),
  columnEndIndex: z.number(),
});

const TableSelectionEditingSchema = z.object({
  rowId: z.string().min(1),
  columnId: z.string().min(1),
  inlineRange: z.object({
    index: z.number().int().nonnegative(),
    length: z.number().int().nonnegative(),
  }),
});

export type TableSelectionEditing = z.TypeOf<
  typeof TableSelectionEditingSchema
>;

const TableSelectionEditingDataSchema = z.object({
  editing: TableSelectionEditingSchema.optional(),
});

export type TableAreaSelection = z.TypeOf<typeof TableAreaSelectionSchema> & {
  editing?: TableSelectionEditing;
};

const TableRowSelectionSchema = z.object({
  type: z.literal('row'),
  rowId: z.string(),
});

const TableColumnSelectionSchema = z.object({
  type: z.literal('column'),
  columnId: z.string(),
});

const TableSelectionDataSchema = z.intersection(
  z.union([
    TableAreaSelectionSchema,
    TableRowSelectionSchema,
    TableColumnSelectionSchema,
  ]),
  TableSelectionEditingDataSchema
);

export type TableSelectionData = z.TypeOf<typeof TableSelectionDataSchema>;
export const TableSelectionData = {
  equals(a?: TableSelectionData, b?: TableSelectionData) {
    if (a === b) {
      return true;
    }
    if (a == null || b == null) {
      return a === b;
    }
    if (a.type !== b.type || !editingEquals(a.editing, b.editing)) {
      return false;
    }
    if (a.type === 'area' && b.type === 'area') {
      return (
        a.rowStartIndex === b.rowStartIndex &&
        a.rowEndIndex === b.rowEndIndex &&
        a.columnStartIndex === b.columnStartIndex &&
        a.columnEndIndex === b.columnEndIndex
      );
    }
    if (a.type === 'row' && b.type === 'row') {
      return a.rowId === b.rowId;
    }
    if (a.type === 'column' && b.type === 'column') {
      return a.columnId === b.columnId;
    }
    return false;
  },
};

function editingEquals(
  a?: TableSelectionEditing,
  b?: TableSelectionEditing
): boolean {
  return (
    a === b ||
    (a != null &&
      b != null &&
      a.rowId === b.rowId &&
      a.columnId === b.columnId &&
      a.inlineRange.index === b.inlineRange.index &&
      a.inlineRange.length === b.inlineRange.length)
  );
}

const TableSelectionSchema = z.object({
  blockId: z.string(),
  data: TableSelectionDataSchema,
});

export class TableSelection extends BaseSelection {
  static override group = 'note';

  static override type = 'table';

  static override recoverable = true;

  readonly data: TableSelectionData;

  constructor({
    blockId,
    data,
  }: {
    blockId: string;
    data: TableSelectionData;
  }) {
    super({
      blockId,
    });

    this.data = data;
  }

  static override fromJSON(json: Record<string, unknown>): TableSelection {
    const parsed = TableSelectionSchema.parse(json);
    return new TableSelection({
      blockId: parsed.blockId,
      data: parsed.data,
    });
  }

  override equals(other: BaseSelection): boolean {
    if (!(other instanceof TableSelection)) {
      return false;
    }
    return (
      this.blockId === other.blockId &&
      TableSelectionData.equals(this.data, other.data)
    );
  }

  override toJSON(): Record<string, unknown> {
    return {
      type: 'table',
      blockId: this.blockId,
      data: this.data,
    };
  }
}

export const TableSelectionExtension = SelectionExtension(TableSelection);
